// Location landing pages for secondlifesoftware.com, rendered by LocationPage.js.
// These target local + "near me" search and local LLM answers ("software
// developers in San Francisco / Atlanta"). We're a remote-first US studio with
// a focus on these two metros — the copy is honest about that.
//
// To add a location: add an object here, add its path to reactSnap.include in
// package.json. It's auto-added to the sitemap via scripts/gen-sitemap.js.

const locations = [
  {
    slug: 'software-development-san-francisco',
    city: 'San Francisco',
    region: 'CA',
    title: 'Software Development in San Francisco',
    metaDescription:
      'MVP and custom software development for San Francisco Bay Area founders and startups. Senior US-based team, no tech background needed. Book a free call.',
    tagline: 'MVP & custom software for San Francisco Bay Area founders.',
    intro:
      'We help San Francisco and Bay Area founders turn ideas into shipped products — MVPs, custom software, and AI tools built by a senior US-based team.',
    body: `San Francisco runs on new products. If you're a founder in the Bay Area with an idea — or a business that needs custom software — Second Life Software builds it, end to end. We're a US-based studio working with SF founders remotely, so you get senior talent and Bay Area availability without Bay Area agency overhead.

## What we build for SF founders

- **MVPs** to validate a new idea with real users, fast.
- **Custom software & internal tools** tailored to how your team actually works.
- **AI-powered apps** built on modern large language models.
- **Web applications** from front end to infrastructure.

## Built for the non-technical founder

Plenty of great SF ideas come from people who don't code — operators, domain experts, first-time founders. We translate your vision into a technical plan and handle the build, so you can focus on customers and fundraising instead of managing engineers you can't evaluate.

## Working with a remote-first studio

We work with Bay Area clients the same way the best distributed teams do: clear scope, working software every two weeks, and direct access to the senior people building your product. You own all the code, the domain, and the cloud accounts.

## Ready when you are

Whether you're pre-seed validating an idea or an established SF business that needs a real tool built, we'll give you a straight answer on scope, cost, and timeline.`,
    faq: [
      {
        q: 'Do you work with San Francisco startups remotely?',
        a: 'Yes. We\'re a remote-first US studio and work with Bay Area founders regularly, with overlapping hours and direct access to the team building your product.',
      },
      {
        q: 'How much does it cost to build an MVP in San Francisco?',
        a: 'Most MVPs land in the $15,000–$40,000 range regardless of city. Working with a senior remote studio avoids paying Bay Area agency overhead.',
      },
    ],
  },
  {
    slug: 'software-development-atlanta',
    city: 'Atlanta',
    region: 'GA',
    title: 'Software Development in Atlanta',
    metaDescription:
      'MVP and custom software development for Atlanta founders and businesses. Senior US-based team, no tech background needed. Rated 5.0 on Google. Book a free call.',
    tagline: 'MVP & custom software for Atlanta founders and businesses.',
    intro:
      'We help Atlanta founders and businesses turn ideas into shipped products — MVPs, custom software, and AI tools built by a senior US-based team.',
    body: `Atlanta's startup and small-business scene is booming, and building software shouldn't mean flying talent in from the coasts. Second Life Software builds MVPs, custom software, and AI tools for Atlanta founders and businesses — a senior US-based team you can actually talk to.

## What we build for Atlanta founders

- **MVPs** to get a credible product in front of real users quickly.
- **Custom software & internal tools** that replace spreadsheets and manual work.
- **AI-powered apps and automation** built on modern models.
- **Web applications** built and maintained end to end.

## For founders and established businesses alike

Whether you're a first-time founder with an idea or an established Atlanta business that's outgrown off-the-shelf tools, we scope tightly, build in two-week cycles, and hand over software you fully own — code, domain, and accounts included.

## Local roots, senior team

We're reachable at (770) 696-3187 and rated 5.0 on Google. You work directly with the senior people building your product — no account managers, no junior bait-and-switch.

## Let's talk

Tell us what you're trying to build and we'll give you an honest scope, cost range, and timeline — no jargon, no obligation.`,
    faq: [
      {
        q: 'Do you offer software development services in Atlanta?',
        a: 'Yes. We work with Atlanta founders and businesses on MVPs, custom software, AI tools, and web apps. Call us at (770) 696-3187 or book a free consultation.',
      },
      {
        q: 'How much does it cost to build an app in Atlanta?',
        a: 'Most MVPs land in the $15,000–$40,000 range; simpler validation builds can start lower. We give a clear written scope and range before any work begins.',
      },
    ],
  },
];

export default locations;

export function getLocationBySlug(slug) {
  return locations.find((l) => l.slug === slug);
}
